// The email templates the OS itself sends with, as opposed to templates
// a person picks in the composer (Documents tab PR 8f).
//
// They live in the same communication_templates library so their wording
// is editable without a deploy (the automation-first rule), but each one
// carries tokens only its own sender fills in (the sign button, the
// deadline sentence). Picked in the ordinary composer, they would send a
// client a literal "{{review_and_sign_button}}". So the composer's and
// the automation builder's pickers leave them out; the templates library
// still lists them, which is where they are edited.
//
// Pure: imported by the web pickers and testable without a DB.

import { CHECKIN_TASK_SLUGS } from "../marketing/checkin";
import { NOT_SURE_TASK } from "../marketing/one-question";
import { THANK_YOU_FALLBACKS } from "../marketing/thank-you";
import { TODAY_EMAIL_FALLBACKS } from "../marketing/today-email";
import { SOA_INVITATION_TEMPLATE_SLUG } from "../planning/soa-invitation-email";
import { SIGNING_INVITATION_TEMPLATE_SLUG } from "./signing-invitation";

/** Mirrors ESIGN_REMINDER_TEMPLATE_SLUG in shared/db/esign-reminders.ts,
 *  spelled out here because that module reaches the database. */
const ESIGN_REMINDER_SLUG = "esign-signature-reminder";

export const SYSTEM_EMAIL_TEMPLATE_SLUGS: ReadonlySet<string> = new Set([
  SIGNING_INVITATION_TEMPLATE_SLUG,
  SOA_INVITATION_TEMPLATE_SLUG,
  ESIGN_REMINDER_SLUG,
  // The Today row's "Write the email" starting wording (Marketing Hub
  // 2.3b-ii): its {{first_name}}, {{product}}, {{company_name}} and
  // {{sender_first_name}} are filled only by that button. Read from the
  // button's own table so a template added there can't be forgotten here.
  ...Object.values(TODAY_EMAIL_FALLBACKS).map((t) => t.slug),
  // The task wording a "Not sure" tap leaves the advisor: not an email at all.
  NOT_SURE_TASK.slug,
  // The tasks an introduction on the annual check-in leaves (row 3.7).
  ...CHECKIN_TASK_SLUGS,
  // "Thank personally" (Marketing Hub 2.6): a suggested wording for a card
  // written by hand, shown beside the row; its merge fields are filled only there.
  ...Object.values(THANK_YOU_FALLBACKS).map((t) => t.slug),
]);

/** Whether a template belongs in a person's template picker. */
export function isPickableTemplate(t: { slug: string }): boolean {
  return !SYSTEM_EMAIL_TEMPLATE_SLUGS.has(t.slug);
}
